import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { CheckCircle, AlertTriangle, XCircle, Info, X } from 'lucide-react';
import i18n from '../../i18n';
import './MessageBox.css';

const MessageBoxContext = createContext(null);

function escapeHtml(str) {
  if (typeof str !== 'string') return String(str ?? '');
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

const VARIANT_CONFIG = {
  success: { icon: CheckCircle, color: 'var(--accent-green, #10B981)', className: 'mb-success' },
  error:   { icon: XCircle,     color: 'var(--accent-red, #EF4444)',   className: 'mb-error' },
  danger:  { icon: XCircle,     color: 'var(--accent-red, #EF4444)',   className: 'mb-danger' },
  warning: { icon: AlertTriangle, color: 'var(--accent-orange, #F59E0B)', className: 'mb-warning' },
  info:    { icon: Info,         color: 'var(--accent-blue, #3B82F6)',  className: 'mb-info' },
  default: { icon: Info,         color: 'var(--accent-blue, #3B82F6)',  className: 'mb-default' },
};

let toastIdCounter = 0;

export function MessageBoxProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [dialog, setDialog] = useState(null);
  const dialogResolveRef = useRef(null);
  const dialogInputRef = useRef(null);
  const dialogConfirmRef = useRef(null);
  const queueRef = useRef([]);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t));
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 300);
  }, []);

  const toast = useCallback((message, type = 'info') => {
    const id = ++toastIdCounter;
    const newToast = { id, message: escapeHtml(message), rawMessage: message, type, exiting: false };
    setToasts(prev => [...prev.slice(-4), newToast]);
    setTimeout(() => removeToast(id), 5000);
  }, [removeToast]);

  const processQueue = useCallback(() => {
    if (queueRef.current.length === 0) return;
    const next = queueRef.current.shift();
    next();
  }, []);

  const closeDialog = useCallback((result) => {
    setDialog(prev => prev ? { ...prev, exiting: true } : null);
    setTimeout(() => {
      setDialog(null);
      if (dialogResolveRef.current) {
        dialogResolveRef.current(result);
        dialogResolveRef.current = null;
      }
      processQueue();
    }, 200);
  }, [processQueue]);

  const showDialog = useCallback((config) => {
    return new Promise((resolve) => {
      const doShow = () => {
        dialogResolveRef.current = resolve;
        setDialog({ ...config, exiting: false });
      };
      if (dialog) {
        queueRef.current.push(doShow);
      } else {
        doShow();
      }
    });
  }, [dialog]);

  const confirm = useCallback((opts = {}) => {
    const variant = opts.variant || 'default';
    const t = (key, fallback) => i18n.t(key, fallback);
    return showDialog({
      type: 'confirm',
      title: opts.title || t('messageBox.btnConfirm', 'Confirm'),
      message: opts.message || t('messageBox.areYouSure', 'Are you sure?'),
      variant,
      confirmLabel: opts.confirmLabel || t('messageBox.btnConfirm', 'Confirm'),
      cancelLabel: opts.cancelLabel || t('messageBox.btnCancel', 'Cancel'),
    });
  }, [showDialog]);

  const prompt = useCallback((opts = {}) => {
    const t = (key, fallback) => i18n.t(key, fallback);
    return showDialog({
      type: 'prompt',
      title: opts.title || t('messageBox.inputRequired', 'Input Required'),
      message: opts.message || '',
      placeholder: opts.placeholder || '',
      defaultValue: opts.defaultValue || '',
      inputType: opts.inputType || 'text',
      selectOptions: opts.selectOptions || [],
      required: opts.required !== false,
      validator: opts.validator || null,
      confirmLabel: opts.confirmLabel || t('messageBox.btnOk', 'OK'),
      cancelLabel: opts.cancelLabel || t('messageBox.btnCancel', 'Cancel'),
      variant: 'default',
    });
  }, [showDialog]);

  const alertModal = useCallback((opts = {}) => {
    const variant = opts.variant || 'info';
    const t = (key, fallback) => i18n.t(key, fallback);
    return showDialog({
      type: 'alert',
      title: opts.title || t('messageBox.notice', 'Notice'),
      message: opts.message || '',
      variant,
      confirmLabel: opts.confirmLabel || t('messageBox.btnOk', 'OK'),
    });
  }, [showDialog]);

  useEffect(() => {
    const api = {
      toast,
      confirm,
      prompt,
      messageBox: { alert: alertModal },
    };
    window.Matrix = window.Matrix || {};
    Object.assign(window.Matrix, api);
    return () => {
      if (window.Matrix) {
        delete window.Matrix.toast;
        delete window.Matrix.confirm;
        delete window.Matrix.prompt;
        delete window.Matrix.messageBox;
      }
    };
  }, [toast, confirm, prompt, alertModal]);

  const value = { toast, confirm, prompt, alertModal };

  return (
    <MessageBoxContext.Provider value={value}>
      {children}
      <ToastRenderer toasts={toasts} onClose={removeToast} />
      {dialog && (
        <DialogRenderer
          dialog={dialog}
          onClose={closeDialog}
          inputRef={dialogInputRef}
          confirmRef={dialogConfirmRef}
        />
      )}
    </MessageBoxContext.Provider>
  );
}

function ToastRenderer({ toasts, onClose }) {
  if (!toasts.length) return null;
  return (
    <div className="mb-toast-container" aria-live="polite">
      {toasts.map(t => (
        <ToastItem key={t.id} toast={t} onClose={onClose} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onClose }) {
  const cfg = VARIANT_CONFIG[toast.type] || VARIANT_CONFIG.info;
  const Icon = cfg.icon;
  return (
    <div className={`mb-toast ${cfg.className} ${toast.exiting ? 'mb-toast-exit' : 'mb-toast-enter'}`} role="status">
      <div className="mb-toast-icon" style={{ color: cfg.color }}>
        <Icon size={20} />
      </div>
      <div className="mb-toast-message">{toast.rawMessage}</div>
      <button className="mb-toast-close" onClick={() => onClose(toast.id)} aria-label={i18n.t('common.close', 'Close')}>
        <X size={14} />
      </button>
    </div>
  );
}

function DialogRenderer({ dialog, onClose, inputRef, confirmRef }) {
  const [inputValue, setInputValue] = useState(dialog.defaultValue || '');
  const [inputError, setInputError] = useState('');
  const backdropRef = useRef(null);

  useEffect(() => {
    setInputValue(dialog.defaultValue || '');
    setInputError('');
  }, [dialog]);

  useEffect(() => {
    if (dialog.type === 'prompt' && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.select) inputRef.current.select();
    } else if (confirmRef.current) {
      confirmRef.current.focus();
    }
  }, [dialog, inputRef, confirmRef]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (dialog.type === 'alert') {
          onClose(undefined);
        } else if (dialog.type === 'confirm') {
          onClose(false);
        } else {
          onClose(null);
        }
      }
      if (e.key === 'Enter') {
        if (dialog.type === 'confirm') {
          onClose(true);
        } else if (dialog.type === 'alert') {
          onClose(undefined);
        } else if (dialog.type === 'prompt') {
          handlePromptSubmit();
        }
      }
      if (e.key === 'Tab') {
        const focusable = backdropRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable && focusable.length > 0) {
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  });

  const handlePromptSubmit = () => {
    const val = inputValue.trim();
    if (dialog.required && !val) {
      setInputError(i18n.t('messageBox.fieldRequired', 'This field is required'));
      return;
    }
    if (dialog.validator) {
      const err = dialog.validator(val);
      if (err) {
        setInputError(err);
        return;
      }
    }
    onClose(val || inputValue);
  };

  const handleBackdropClick = (e) => {
    if (e.target === backdropRef.current) {
      if (dialog.type === 'alert') onClose(undefined);
      else if (dialog.type === 'confirm') onClose(false);
      else onClose(null);
    }
  };

  const variant = dialog.variant || 'default';
  const cfg = VARIANT_CONFIG[variant] || VARIANT_CONFIG.default;
  const Icon = cfg.icon;

  const btnVariantClass = variant === 'danger' ? 'mb-btn-danger'
    : variant === 'warning' ? 'mb-btn-warning'
    : 'mb-btn-primary';

  return (
    <div
      className={`mb-backdrop ${dialog.exiting ? 'mb-backdrop-exit' : 'mb-backdrop-enter'}`}
      ref={backdropRef}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="mb-dialog-title"
    >
      <div className={`mb-dialog ${cfg.className} ${dialog.exiting ? 'mb-dialog-exit' : 'mb-dialog-enter'}`}>
        <div className="mb-dialog-header">
          <div className="mb-icon-wrapper" style={{ backgroundColor: cfg.color + '18', color: cfg.color }}>
            <Icon size={24} />
          </div>
          <h3 className="mb-title" id="mb-dialog-title">{dialog.title}</h3>
        </div>

        {dialog.message && (
          <p className="mb-message">{dialog.message}</p>
        )}

        {dialog.type === 'prompt' && (
          <div className="mb-input-wrapper">
            {dialog.inputType === 'select' ? (
              <select
                ref={inputRef}
                className="mb-input mb-select"
                value={inputValue}
                onChange={(e) => { setInputValue(e.target.value); setInputError(''); }}
              >
                <option value="">{dialog.placeholder || i18n.t('messageBox.selectPlaceholder', '— Select —')}</option>
                {(dialog.selectOptions || []).map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            ) : (
              <input
                ref={inputRef}
                className="mb-input"
                type={dialog.inputType || 'text'}
                placeholder={dialog.placeholder}
                value={inputValue}
                onChange={(e) => { setInputValue(e.target.value); setInputError(''); }}
              />
            )}
            {inputError && <div className="mb-input-error">{inputError}</div>}
          </div>
        )}

        <div className="mb-actions">
          {dialog.type !== 'alert' && (
            <button
              className="mb-btn mb-btn-ghost"
              onClick={() => onClose(dialog.type === 'confirm' ? false : null)}
            >
              {dialog.cancelLabel || i18n.t('messageBox.btnCancel', 'Cancel')}
            </button>
          )}
          <button
            ref={confirmRef}
            className={`mb-btn ${btnVariantClass}`}
            onClick={() => {
              if (dialog.type === 'confirm') onClose(true);
              else if (dialog.type === 'alert') onClose(undefined);
              else handlePromptSubmit();
            }}
          >
            {dialog.confirmLabel || i18n.t('messageBox.btnOk', 'OK')}
          </button>
        </div>
      </div>
    </div>
  );
}

export function useMessageBox() {
  const ctx = useContext(MessageBoxContext);
  if (!ctx) throw new Error('useMessageBox must be used within MessageBoxProvider');
  return ctx;
}

export default MessageBoxProvider;
